import React, { FC, useContext, useEffect, useMemo, useRef, useState } from 'react';
import InfiniteScroller from 'components/molecules/InfiniteScroller';
import { useFillHeightToFitScreen } from 'hooks/general/useFillHeightToFitScreen';
import { CustomTable } from './PricingSimulatorIndexTableWrapper';
import ErrorBox from 'components/molecules/ErrorBox/ErrorBox';
import { FilterContextData } from 'contexts/FilterContext';
import { UserContext } from 'contexts/ConfigContext';
import { getAuthUser, getConfig } from 'utils/helperMethods';
import { getAnalyticsTableColumns } from 'utils/getPricingSimulatorTableColumns';
import { pricingSimulatorDataContext } from 'contexts/PricingSimulatorContext';
import { pricingAnalyticsUrls } from 'config/urls';
import { useCommonApiHandler } from 'hooks/general/useCommonApiHandler';
import dayjs from 'dayjs';
import { useFetchReportTableData } from 'hooks/pricingSimulator/analytics/useFetchReportTableData';
import { queryClient } from 'services/reactQueryService';

const analyticsTableId = 'scenario-analytics-table-id';

type Props = {
    tableReportsReactQueryVariables: {
        search_keyword: string;
        limit: number;
        offset: number;
        sort_by: string;
        sorting_order: string;
        user_name: string;
    };
    setTableReportsReactQueryVariables: Function;
};

const AnalyticsIndexTable: FC<Props> = ({ tableReportsReactQueryVariables, setTableReportsReactQueryVariables }) => {
    const { appConfig } = useContext(UserContext);
    const pricingSimulatorConfig = getConfig('pricingSimulator', appConfig);
    const { loggedInUser } = useContext(pricingSimulatorDataContext);
    const analyticsTableColumns = pricingSimulatorConfig.analyticsTableColumns;
    const enablePrivatePublicToggle = pricingSimulatorConfig.enablePrivatePublicToggle;
    const mainSectionHeight = useRef<number | null>(0);

    //filter context
    const { allDimAndAttributeTypeList, timePeriodSelection, timePeriodLoading } = useContext(FilterContextData);

    const enabledVariable = Boolean(
        allDimAndAttributeTypeList &&
            'loading' in allDimAndAttributeTypeList &&
            !timePeriodLoading &&
            timePeriodSelection,
    );

    //bottom chart 1
    const {
        data: reportListData,
        count: reportListCount,
        loading: reportsListLoading,
        error: reportsListError,
    } = useFetchReportTableData({
        method: 'post',
        queryConfig: { enabled: Boolean(enabledVariable) },
        url: pricingAnalyticsUrls.getReportsListUrl,
        queryKey: ['pa-index-table-reports', tableReportsReactQueryVariables],
        variables: { ...(tableReportsReactQueryVariables || {}) },
    });
    const memoisedColumns = useMemo(() => {
        return getAnalyticsTableColumns(
            analyticsTableColumns,
            loggedInUser,
            {
                sortBy: tableReportsReactQueryVariables?.sort_by || 0,
                sortOrder:
                    tableReportsReactQueryVariables?.sorting_order === 'asc'
                        ? 'ascend'
                        : tableReportsReactQueryVariables?.sorting_order === 'desc'
                        ? 'descend'
                        : null,
            },
            enablePrivatePublicToggle,
        );
    }, [reportListData]);

    //cleanup
    useEffect(() => {
        return () => {
            queryClient.removeQueries({
                exact: false,
                queryKey: ['pa-index-table-reports'],
            });
        };
    }, []);

    const commonLoading = reportsListLoading || timePeriodLoading;

    function infiniteScrollOnNext(loading: boolean) {
        if (!loading) {
            const newOffset = tableReportsReactQueryVariables.offset + tableReportsReactQueryVariables.limit;
            setTableReportsReactQueryVariables({ ...tableReportsReactQueryVariables, offset: newOffset });
        }
    }

    //this hook with give height to fill the screen height
    mainSectionHeight.current = useFillHeightToFitScreen({
        id: analyticsTableId,
        height: mainSectionHeight.current,
        buffer: 80,
        // triggerDummyRender: true,
    });
    return (
        <div>
            <InfiniteScroller
                data={reportListData}
                loading={commonLoading}
                hasMore={reportListData?.length < reportListCount || commonLoading}
                height={reportListData && !reportsListError ? mainSectionHeight.current ?? undefined : 'auto'}
                //height={200}
                //     loader={true}
                onNext={() => infiniteScrollOnNext(commonLoading)}
            >
                <CustomTable
                    onChange={(pagination, filter, sort: any, extra) => {
                        if (sort?.order) {
                            const sortMapping: any = {
                                descend: 'desc',
                                ascend: 'asc',
                            };

                            setTableReportsReactQueryVariables({
                                ...tableReportsReactQueryVariables,
                                sort_by: sort?.column?.sortKey,
                                sorting_order: sortMapping?.[sort?.order] || 'desc',
                                offset: 0,
                            });
                            //default it to dim1 column on cancelling sorting
                        } else {
                            setTableReportsReactQueryVariables({
                                ...tableReportsReactQueryVariables,
                                sort_by: 'updated_at',
                                sorting_order: 'desc',
                                offset: 0,
                            });
                        }
                    }}
                    sticky
                    id={analyticsTableId}
                    //loading={commonLoading}
                    columns={memoisedColumns}
                    scroll={{ x: '100%' }}
                    dataSource={reportListData}
                    pagination={false}
                    style={{
                        opacity: commonLoading ? '0.3' : 1,
                        pointerEvents: commonLoading ? 'none' : 'auto',
                    }}
                    locale={{
                        emptyText: reportsListError ? <ErrorBox msg={String(reportsListError)} /> : '',
                    }}
                />
            </InfiniteScroller>
        </div>
    );
};

export default AnalyticsIndexTable;
